/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Card, Checkbox, Collapse, Form, Radio, Row, Space, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import EntryPointSelect from '../../../Forms/Fields/EntryPointSelect';
import LabelWithOriginalText from '../../../Forms/Fields/LabelWithOriginalText';
import RichTextEditor from '../../../Forms/Fields/RichTextEditor/RichTextEditor';
import { ArrowDownIcon } from '../../../Icons/ArrowDownIcon';

// Misc imports
import { ACTIVITY_TYPES, VARIABLE_TYPES } from '../../../../constants/main';
import { ANALYTIC_TAGS_RECOMMENDATIONS } from '../../../../constants/flowGraph';
import { GraphContext } from '../../FlowGraph';
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import { getCurrentQueryState } from '../../../../store/utils/getCurrentQueryState';
import { useParams, useSearchParams } from 'react-router-dom';

const MessageNodeForm = ({ form, selectedElement }) => {
  const { t } = useTranslation();
  const { isEditingPrimaryLanguage, botLanguages, currentBotLanguage, primaryBotLanguage } = useBotLanguage();
  const { elements } = useContext(GraphContext);
  const { orgId, tenantId } = useParams();
  const [searchParams] = useSearchParams();

  const { data: activity } = getCurrentQueryState('fetchActivity', { orgId, tenantId, activityId: searchParams.get('activity'), currentBotLanguage }, !searchParams.get('activity'));

  // Check for outgoing edges from the node - if there are none, we can consider it a leaf node
  var isLeafNode = !elements.some(element => element.data && element.data.nodeType === 'edge' && element.source === selectedElement.id);

  const isThumbsFeedbackActive = () => {
    if(form.getFieldValue(['data', 'inlineFeedbackData', 'enabled']) === false) {
      form.setFieldValue(['data', 'enableThumbsFeedback'], false);
      return false;
    }

    if(form.getFieldValue(['data', 'inlineFeedbackData', 'enabled']) === true) {
      form.setFieldValue(['data', 'enableThumbsFeedback'], true);
      return true;
    }

    return undefined;
  };

  return (
    <Fragment>
      <LabelWithOriginalText label={t('Text')} originalText={form.getFieldValue(['data', 'label', primaryBotLanguage])} />
      {
        botLanguages.map(lang => (
          <Form.Item
            label=""
            name={['data', 'label', lang]}
            hidden={currentBotLanguage !== lang}
            rules={[{ required: lang === primaryBotLanguage && isEditingPrimaryLanguage, message: t('Message cannot be blank.') }]}
          >
            <RichTextEditor
              placeholder={t('Write some content')}
              includedVariableTypes={activity?.type === ACTIVITY_TYPES.LIVECHAT_FLOW ? [VARIABLE_TYPES.LIVECHAT] : []}
            />
          </Form.Item>
        ))
      }

      <Collapse
        bordered={false}
        expandIcon={({ isActive }) => <ArrowDownIcon rotate={isActive ? 180 : 0} />}
      >
        <Collapse.Panel
          header={t('Advanced Settings')}
          forceRender
        >
          <Form.Item
            label={t('Thumb Rating')}
            name={['data', 'inlineFeedbackData', 'enabled']}
            valuePropName="checked"
          >
            <Radio.Group
              className="w-100"
              defaultValue={form.getFieldValue(['data', 'inlineFeedbackData', 'enabled'])}
              onChange={(e) => {
                const { value } = e.target;

                if (value === undefined) {
                  form.setFieldValue(['data', 'inlineFeedbackData'], value);
                } else {
                  form.setFieldValue(['data', 'inlineFeedbackData'], { enabled: value });
                }
              }}
            >
              <Space direction="vertical" className="w-100">
                <Radio value={true} className="w-100">
                  <Row className="w-100">
                    {t('Enable Thumb Rating')}
                    <Tooltip
                      placement="right"
                      title={t('This action will overwrite any Thumb Rating configurations made in the agent\'s settings page.')}
                      className="ml-1"
                    >
                      <InfoCircleOutlined />
                    </Tooltip>

                    {
                      isThumbsFeedbackActive() ? (
                        <Card
                          size="small"
                          bordered
                          className="mt-2 w-100 shadow-none"
                        >
                          <Form.Item
                            hidden
                            name={['data', 'inlineFeedbackData', 'positive', 'tags']}
                            initialValue={[ANALYTIC_TAGS_RECOMMENDATIONS.USER_FEEDBACK_POSITIVE]}
                          />

                          <Form.Item
                            label={t('Flow triggered with Thumbs Up')}
                            name={['data', 'inlineFeedbackData', 'positive', 'feedbackNodeId']}
                          >
                            <EntryPointSelect
                              allowClear={true}
                              activityTypesToDisplay={Object.values(ACTIVITY_TYPES)}
                              disabled={!isEditingPrimaryLanguage}
                            />
                          </Form.Item>

                          <Form.Item
                            hidden
                            name={['data', 'inlineFeedbackData', 'negative', 'tags']}
                            initialValue={[ANALYTIC_TAGS_RECOMMENDATIONS.USER_FEEDBACK_NEGATIVE]}
                          />

                          <Form.Item
                            label={t('Flow triggered with Thumbs Down')}
                            name={['data', 'inlineFeedbackData', 'negative', 'feedbackNodeId']}
                          >
                            <EntryPointSelect
                              allowClear={true}
                              activityTypesToDisplay={Object.values(ACTIVITY_TYPES)}
                              disabled={!isEditingPrimaryLanguage}
                            />
                          </Form.Item>
                        </Card>
                      ) : null
                    }
                  </Row>
                </Radio>

                <Radio value={false}>
                  {t('Disable Thumb Rating')}
                  <Tooltip
                    placement="right"
                    title={t('This action will overwrite any Thumb Rating configurations made in the agent\'s settings page.')}
                    className="ml-1"
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </Radio>

                <Radio value={undefined}>{t('Default')}</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          {
            // Only show back button setting if the message is a leaf node (is the last one in the graph)
            // Also do not show the option on the initial edit (ie. the selectedElement has no content)
            isLeafNode && selectedElement.data.label[currentBotLanguage] ? (
              <Form.Item
                label=""
                name={['data', 'enableBackAction']}
                valuePropName="checked"
                className="mb-0"
              >
                <Checkbox disabled={!isEditingPrimaryLanguage}>
                  {t('Enable back button')}
                </Checkbox>
              </Form.Item>
            ) : null
          }
        </Collapse.Panel>
      </Collapse>
    </Fragment>
  );
};

MessageNodeForm.propTypes = {
  form: PropTypes.object.isRequired,
  selectedElement: PropTypes.object.isRequired
};

export default MessageNodeForm;
